import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  CalendarClock,
  HelpCircle,
  Loader2,
  Mail,
  MessageCircle,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import LogoLoader from '@/components/LogoLoader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { formatDisplayDateTime } from '@/utils/date';
import { api } from '@/utils/api';
import { Textarea } from '@/components/ui/textarea';
import { communicationsHelpEntries, communicationsHelpLookup } from '@/help/communications';

const createContextRow = () => ({ key: '', value: '' });

const createEmailForm = (templateKey = '') => ({
  to_address: '',
  template_key: templateKey,
  invoice_id: '',
  context: [createContextRow()],
});

const createPortalForm = (templateKey = '') => ({
  template_key: templateKey,
  invoice_id: '',
  context: [createContextRow()],
});

const buildTemplateFormState = (template) => {
  const latestInvoiceRule = Array.isArray(template?.attachment_rules)
    ? template.attachment_rules.find((rule) => rule.kind === 'latest-invoice-pdf')
    : null;

  return {
    template_key: template?.template_key ?? '',
    subject: template?.subject ?? '',
    body: template?.body ?? '',
    attach_latest_invoice: Boolean(latestInvoiceRule),
    latest_invoice_filename: latestInvoiceRule?.filename_template ?? '',
  };
};

const createTemplateForm = () => buildTemplateFormState(null);

const describeAttachmentRule = (rule) => {
  if (!rule) {
    return '';
  }

  if (rule.kind === 'latest-invoice-pdf') {
    const base = 'Latest invoice PDF';
    return rule.filename_template
      ? `${base} (${rule.filename_template})`
      : base;
  }

  return rule.kind;
};

const defaultAutomationSettings = {
  enabled: false,
  trigger_invoice_statuses: ['sent', 'overdue'],
  days_before_due: null,
  days_after_due: null,
  po_hours_remaining_threshold: null,
  payment_acknowledgements_enabled: false,
  payment_acknowledgement_template: 'invoice-payment-received',
  payment_acknowledgement_overrides: {},
};

const defaultEmailSettings = {
  from_address: '',
  provider_type: 'logging',
  credentials: {
    host: '',
    port: '587',
    username: '',
    password: '',
    use_tls: true,
    api_key: '',
  },
};

const providerOptions = [
  { value: 'logging', label: 'Log activity only' },
  { value: 'smtp', label: 'SMTP relay' },
  { value: 'sendgrid', label: 'SendGrid API' },
];

const invoiceStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

const sortStatuses = (statuses) => {
  const order = invoiceStatusOptions.map((option) => option.value);
  return [...new Set(statuses)].sort(
    (a, b) => order.indexOf(a) - order.indexOf(b)
  );
};

const normalizeAutomationSettings = (settings) => {
  if (!settings || typeof settings !== 'object') {
    return { ...defaultAutomationSettings };
  }

  const normalizedStatuses = Array.isArray(settings.trigger_invoice_statuses)
    ? settings.trigger_invoice_statuses.map(String)
    : defaultAutomationSettings.trigger_invoice_statuses;

  const rawOverrides =
    typeof settings.payment_acknowledgement_overrides === 'object' &&
    settings.payment_acknowledgement_overrides !== null
      ? settings.payment_acknowledgement_overrides
      : {};
  const normalizedOverrides = Object.entries(rawOverrides).reduce(
    (acc, [key, value]) => {
      const normalizedKey = String(key || '').trim();
      if (!normalizedKey) {
        return acc;
      }
      acc[normalizedKey] = Boolean(value);
      return acc;
    },
    {}
  );

  return {
    enabled: Boolean(settings.enabled),
    trigger_invoice_statuses: sortStatuses(
      normalizedStatuses.length > 0
        ? normalizedStatuses
        : defaultAutomationSettings.trigger_invoice_statuses
    ),
    days_before_due:
      settings.days_before_due === null || settings.days_before_due === undefined
        ? defaultAutomationSettings.days_before_due
        : settings.days_before_due,
    days_after_due:
      settings.days_after_due === null || settings.days_after_due === undefined
        ? defaultAutomationSettings.days_after_due
        : settings.days_after_due,
    po_hours_remaining_threshold:
      settings.po_hours_remaining_threshold === null ||
      settings.po_hours_remaining_threshold === undefined
        ? defaultAutomationSettings.po_hours_remaining_threshold
        : settings.po_hours_remaining_threshold,
    payment_acknowledgements_enabled: Boolean(
      settings.payment_acknowledgements_enabled
    ),
    payment_acknowledgement_template:
      typeof settings.payment_acknowledgement_template === 'string'
        ? settings.payment_acknowledgement_template.trim() ||
          defaultAutomationSettings.payment_acknowledgement_template
        : defaultAutomationSettings.payment_acknowledgement_template,
    payment_acknowledgement_overrides: normalizedOverrides,
  };
};

const formatReminderTypeLabel = (type) => {
  if (!type) {
    return '';
  }
  return type
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatReminderStatusLabel = (status) => {
  if (!status) {
    return 'Sent';
  }
  const lower = status.toLowerCase();
  if (lower.startsWith('override-')) {
    const base = lower.replace('override-', '');
    if (base === 'sent') {
      return 'Sent (override)';
    }
    if (base === 'skipped') {
      return 'Skipped (override)';
    }
    return formatReminderStatusLabel(base);
  }
  if (lower.startsWith('queued') || lower.startsWith('pending')) {
    return 'Pending';
  }
  if (lower.includes('skip')) {
    return 'Skipped';
  }
  if (lower.includes('sent')) {
    return 'Sent';
  }
  return status.replace(/-/g, ' ');
};

const categorizeReminderStatus = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized.startsWith('queued') || normalized.startsWith('pending')) {
    return 'pending';
  }
  if (normalized.includes('skip')) {
    return 'skipped';
  }
  return 'sent';
};

const normalizeEmailSettings = (settings) => {
  if (!settings || typeof settings !== 'object') {
    return {
      ...defaultEmailSettings,
      credentials: { ...defaultEmailSettings.credentials },
    };
  }

  const credentials = {
    ...defaultEmailSettings.credentials,
    ...(settings.credentials ?? {}),
  };

  const providerType = typeof settings.provider_type === 'string'
    ? settings.provider_type
    : defaultEmailSettings.provider_type;

  const normalizedPort =
    credentials.port === undefined || credentials.port === null || credentials.port === ''
      ? defaultEmailSettings.credentials.port
      : String(credentials.port);

  return {
    from_address: settings.from_address ?? defaultEmailSettings.from_address,
    provider_type: providerType,
    credentials: {
      ...credentials,
      port: normalizedPort,
      use_tls:
        credentials.use_tls === undefined || credentials.use_tls === null
          ? defaultEmailSettings.credentials.use_tls
          : Boolean(credentials.use_tls),
    },
  };
};

const buildContextObject = (entries) =>
  entries.reduce((acc, entry) => {
    const key = entry.key.trim();
    if (!key) return acc;
    acc[key] = entry.value;
    return acc;
  }, {});

const parseContextMetadata = (metadata) => {
  if (!metadata || !metadata.context) {
    return [];
  }

  try {
    const raw = metadata.context;
    const parsed =
      typeof raw === 'string'
        ? JSON.parse(raw)
        : typeof raw === 'object' && raw !== null
        ? raw
        : null;
    if (!parsed || typeof parsed !== 'object') {
      return [];
    }
    return Object.entries(parsed);
  } catch (error) {
    return [];
  }
};

const channelStyles = {
  email: 'bg-blue-100 text-blue-800 border-blue-200',
  portal: 'bg-amber-100 text-amber-800 border-amber-200',
};

const sortTemplatesByKey = (items) =>
  [...items].sort((a, b) => a.template_key.localeCompare(b.template_key));

const workflowSteps = [
  {
    id: 'compose',
    title: 'Compose & Send',
    description: 'Draft manual outreach and preview templates in-line.',
    icon: Send,
  },
  {
    id: 'schedule',
    title: 'Schedule & Queue',
    description: 'Tune automation triggers and understand the delivery queue.',
    icon: CalendarClock,
  },
  {
    id: 'monitor',
    title: 'Monitor Status',
    description: 'Confirm what went out, to whom, and when at a glance.',
    icon: Activity,
  },
];

const buildHelpSummary = (entry) => {
  if (!entry) {
    return '';
  }

  const parts = [entry.summary];

  if (Array.isArray(entry.keywords) && entry.keywords.length > 0) {
    parts.push(`Keywords: ${entry.keywords.join(', ')}`);
  }

  if (Array.isArray(entry.relatedTopics) && entry.relatedTopics.length > 0) {
    parts.push(`Related topics: ${entry.relatedTopics.join(', ')}`);
  }

  return parts.join(' ');
};

const helpAttributes = (entry) =>
  entry
    ? {
        'data-help-topic': entry.id,
        'data-help-keywords': Array.isArray(entry.keywords)
          ? entry.keywords.join(',')
          : '',
        'data-help-summary': buildHelpSummary(entry),
      }
    : {};

const Communications = () => {
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState(() => createTemplateForm());
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplateHelp, setShowTemplateHelp] = useState(false);

  const templateHelp = communicationsHelpLookup['communications-template-library'];
  const templatedEmailHelp = communicationsHelpLookup['communications-templated-email'];
  const portalHelp = communicationsHelpLookup['communications-portal-message'];
  const automationHelp = communicationsHelpLookup['communications-automation-settings'];
  const queueHelp = communicationsHelpLookup['communications-queue-activity'];
  const historyHelp = communicationsHelpLookup['communications-history-log'];

  const helpSearchText = useMemo(
    () =>
      communicationsHelpEntries
        .map((entry) => buildHelpSummary(entry))
        .filter(Boolean)
        .join(' '),
    [],
  );
  const [selectedClientId, setSelectedClientId] = useState('');

  const [communicationLogs, setCommunicationLogs] = useState([]);
  const [overridingId, setOverridingId] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [emailForm, setEmailForm] = useState(() => createEmailForm());
  const [portalForm, setPortalForm] = useState(() => createPortalForm());
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [submittingPortal, setSubmittingPortal] = useState(false);
  const [automationSettings, setAutomationSettings] = useState(() =>
    normalizeAutomationSettings(null)
  );
  const [automationLoading, setAutomationLoading] = useState(true);
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [automationDirty, setAutomationDirty] = useState(false);
  const [emailSettings, setEmailSettings] = useState(() =>
    normalizeEmailSettings(null)
  );
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(true);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [emailSettingsDirty, setEmailSettingsDirty] = useState(false);
  const [activeWorkflowStep, setActiveWorkflowStep] = useState(
    workflowSteps[0].id
  );

  useEffect(() => {
    let isMounted = true;

    const loadInitial = async () => {
      setLoading(true);
      setEmailSettingsLoading(true);
      try {
        const [
          clientsData,
          templatesData,
          automationData,
          deliveryData,
        ] = await Promise.all([
          api.get('/clients', token),
          api.get('/accounting/communications/templates', token),
          api.get('/accounting/communications/automation-settings', token),
          api.get('/accounting/communications/email-settings', token),
        ]);

        if (!isMounted) return;

        setClients(clientsData);
        setTemplates(sortTemplatesByKey(templatesData));
        setAutomationSettings(normalizeAutomationSettings(automationData));
        setAutomationDirty(false);
        setEmailSettings(normalizeEmailSettings(deliveryData));
        setEmailSettingsDirty(false);

        if (clientsData.length > 0) {
          const preferred = clientsData.find((client) => client.is_active) ?? clientsData[0];
          setSelectedClientId((current) => current || preferred.id);
        } else {
          setSelectedClientId('');
        }
      } catch (error) {
        if (isMounted) {
          toast.error('Failed to load communications workspace');
          setAutomationSettings(normalizeAutomationSettings(null));
          setAutomationDirty(false);
          setEmailSettings(normalizeEmailSettings(null));
          setEmailSettingsDirty(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setAutomationLoading(false);
          setEmailSettingsLoading(false);
        }
      }
    };

    loadInitial();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const upsertTemplate = useCallback((nextTemplate) => {
    setTemplates((prev) => {
      const filtered = prev.filter(
        (template) => template.template_key !== nextTemplate.template_key
      );
      const combined = [...filtered, nextTemplate];
      return sortTemplatesByKey(combined);
    });
  }, []);

  const startNewTemplate = useCallback(() => {
    setTemplateForm(createTemplateForm());
    setIsEditingTemplate(false);
  }, []);

  const handleTemplateSelect = useCallback(
    (value) => {
      if (value === '__new__') {
        startNewTemplate();
        return;
      }

      const selected = templates.find(
        (template) => template.template_key === value
      );
      if (selected) {
        setTemplateForm(buildTemplateFormState(selected));
        setIsEditingTemplate(true);
      }
    },
    [templates, startNewTemplate]
  );

  const updateAutomationState = useCallback((updater) => {
    setAutomationSettings((prev) => {
      const draft =
        typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      const next = {
        ...prev,
        ...draft,
      };
      const statuses =
        draft.trigger_invoice_statuses ?? prev.trigger_invoice_statuses ?? [];
      next.trigger_invoice_statuses = sortStatuses(statuses);
      return next;
    });
    setAutomationDirty(true);
  }, []);

  const handleEmailFieldChange = useCallback(
    (field) => (valueOrEvent) => {
      const value =
        typeof valueOrEvent === 'string'
          ? valueOrEvent
          : valueOrEvent?.target?.value ?? '';
      setEmailSettings((prev) => ({ ...prev, [field]: value }));
      setEmailSettingsDirty(true);
    },
    [],
  );

  const handleCredentialChange = useCallback(
    (field) => (valueOrEvent) => {
      const value =
        typeof valueOrEvent === 'string'
          ? valueOrEvent
          : valueOrEvent?.target?.value ?? '';
      setEmailSettings((prev) => ({
        ...prev,
        credentials: { ...prev.credentials, [field]: value },
      }));
      setEmailSettingsDirty(true);
    },
    [],
  );

  const handleCredentialToggle = useCallback(
    (field) => (checked) => {
      setEmailSettings((prev) => ({
        ...prev,
        credentials: { ...prev.credentials, [field]: Boolean(checked) },
      }));
      setEmailSettingsDirty(true);
    },
    [],
  );

  const handleProviderChange = useCallback(
    (nextProvider) => {
      setEmailSettings((prev) =>
        normalizeEmailSettings({ ...prev, provider_type: nextProvider })
      );
      setEmailSettingsDirty(true);
    },
    [],
  );

  const handleEmailSettingsSubmit = async (event) => {
    event.preventDefault();
    setSavingEmailSettings(true);
    try {
      const payload = {
        from_address: emailSettings.from_address.trim(),
        provider_type: emailSettings.provider_type,
        credentials: {},
      };

      if (emailSettings.provider_type === 'smtp') {
        const { host, port, username, password, use_tls } = emailSettings.credentials;
        const normalizedPort = (() => {
          if (typeof port === 'number') {
            return String(port);
          }
          const raw = (port ?? defaultEmailSettings.credentials.port).toString().trim();
          return raw || defaultEmailSettings.credentials.port;
        })();
        payload.credentials = {
          host: (host ?? '').trim(),
          port: normalizedPort,
          use_tls: Boolean(use_tls),
        };
        if ((username ?? '').trim()) {
          payload.credentials.username = username.trim();
        }
        if (typeof password === 'string' && password.length > 0) {
          payload.credentials.password = password;
        }
      } else if (emailSettings.provider_type === 'sendgrid') {
        payload.credentials = {
          api_key: (emailSettings.credentials.api_key ?? '').trim(),
        };
      }

      const updated = await api.put(
        '/accounting/communications/email-settings',
        payload,
        token,
      );
      setEmailSettings(normalizeEmailSettings(updated));
      setEmailSettingsDirty(false);
      toast.success('Email delivery settings updated');
    } catch (error) {
      toast.error('Unable to update email delivery settings');
    } finally {
      setSavingEmailSettings(false);
    }
  };

  const handleAutomationToggle = useCallback(
    (checked) => {
      updateAutomationState({ enabled: Boolean(checked) });
    },
    [updateAutomationState]
  );

  const handleAutomationStatusChange = useCallback(
    (status) => (checked) => {
      const isChecked = checked === true || checked === 'indeterminate';
      updateAutomationState((prev) => {
        const exists = prev.trigger_invoice_statuses.includes(status);
        let nextStatuses = prev.trigger_invoice_statuses;
        if (isChecked && !exists) {
          nextStatuses = [...prev.trigger_invoice_statuses, status];
        } else if (!isChecked && exists) {
          nextStatuses = prev.trigger_invoice_statuses.filter(
            (value) => value !== status
          );
        }
        return { ...prev, trigger_invoice_statuses: nextStatuses };
      });
    },
    [updateAutomationState]
  );

  const handleAutomationNumberChange = useCallback(
    (key) => (event) => {
      const raw = event.target.value;
      const numeric = raw === '' ? null : Number(raw);
      if (raw !== '' && Number.isNaN(numeric)) {
        return;
      }
      updateAutomationState({ [key]: numeric });
    },
    [updateAutomationState]
  );

  const handleAcknowledgementToggle = useCallback(
    (checked) => {
      updateAutomationState((prev) => {
        const overrides = { ...(prev.payment_acknowledgement_overrides ?? {}) };
        if (checked) {
          Object.entries(overrides).forEach(([key, value]) => {
            if (value === true) {
              delete overrides[key];
            }
          });
        } else {
          Object.entries(overrides).forEach(([key, value]) => {
            if (value === false) {
              delete overrides[key];
            }
          });
        }
        return {
          payment_acknowledgements_enabled: checked,
          payment_acknowledgement_overrides: overrides,
        };
      });
    },
    [updateAutomationState]
  );

  const handleAcknowledgementTemplateChange = useCallback(
    (event) => {
      updateAutomationState({
        payment_acknowledgement_template: event.target.value,
      });
    },
    [updateAutomationState]
  );

  const handleAcknowledgementOverrideToggle = useCallback(
    (clientId) => (checked) => {
      updateAutomationState((prev) => {
        const overrides = { ...(prev.payment_acknowledgement_overrides ?? {}) };
        const key = String(clientId);
        if (prev.payment_acknowledgements_enabled) {
          if (checked) {
            delete overrides[key];
          } else {
            overrides[key] = false;
          }
        } else if (checked) {
          overrides[key] = true;
        } else {
          delete overrides[key];
        }
        return { payment_acknowledgement_overrides: overrides };
      });
    },
    [updateAutomationState]
  );

  const resolveClientAcknowledgement = useCallback(
    (clientId) => {
      const overrides = automationSettings.payment_acknowledgement_overrides ?? {};
      const override = overrides[String(clientId)];
      if (automationSettings.payment_acknowledgements_enabled) {
        return override !== false;
      }
      return override === true;
    },
    [
      automationSettings.payment_acknowledgements_enabled,
      automationSettings.payment_acknowledgement_overrides,
    ],
  );

  const handleAutomationSave = async (event) => {
    event.preventDefault();
    setSavingAutomation(true);
    try {
      const payload = {
        ...automationSettings,
        trigger_invoice_statuses: sortStatuses(
          automationSettings.trigger_invoice_statuses
        ),
      };
      if (typeof payload.payment_acknowledgement_template === 'string') {
        payload.payment_acknowledgement_template = payload.payment_acknowledgement_template.trim();
      }
      payload.payment_acknowledgement_overrides = Object.entries(
        payload.payment_acknowledgement_overrides ?? {}
      ).reduce((acc, [key, value]) => {
        const normalizedKey = String(key || '').trim();
        if (!normalizedKey) {
          return acc;
        }
        acc[normalizedKey] = Boolean(value);
        return acc;
      }, {});
      const updated = await api.put(
        '/accounting/communications/automation-settings',
        payload,
        token
      );
      setAutomationSettings(normalizeAutomationSettings(updated));
      setAutomationDirty(false);
      toast.success('Automation settings updated');
    } catch (error) {
      toast.error('Unable to update automation settings');
    } finally {
      setSavingAutomation(false);
    }
  };

  const handleTemplateSubmit = async (event) => {
    event.preventDefault();

    const templateKey = templateForm.template_key.trim();
    const subject = templateForm.subject.trim();
    const body = templateForm.body.trim();
    const attachmentRules = [];

    if (templateForm.attach_latest_invoice) {
      const filename = templateForm.latest_invoice_filename.trim();
      attachmentRules.push({
        kind: 'latest-invoice-pdf',
        filename_template: filename ? filename : null,
      });
    }

    if (!templateKey || !subject || !body) {
      toast.error('Template key, subject, and body are required');
      return;
    }

    setSavingTemplate(true);
    try {
      let savedTemplate;
      if (isEditingTemplate) {
        savedTemplate = await api.put(
          `/accounting/communications/templates/${encodeURIComponent(templateKey)}`,
          { subject, body, attachment_rules: attachmentRules },
          token
        );
        toast.success('Template updated');
      } else {
        savedTemplate = await api.post(
          '/accounting/communications/templates',
          { template_key: templateKey, subject, body, attachment_rules: attachmentRules },
          token
        );
        toast.success('Template created');
      }

      upsertTemplate(savedTemplate);
      setTemplateForm(buildTemplateFormState(savedTemplate));
      setIsEditingTemplate(true);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error('Template key already exists. Choose a different key.');
      } else if (status === 404) {
        toast.error('Template could not be found for update.');
      } else if (status === 400) {
        toast.error('Template content is invalid. Check the details and try again.');
      } else {
        toast.error('Failed to save template changes');
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  const loadCommunications = useCallback(
    async (clientId) => {
      if (!clientId) {
        setCommunicationLogs([]);
        return;
      }

      setLoadingLogs(true);
      try {
        const data = await api.get(`/accounting/communications?client_id=${clientId}`, token);
        setCommunicationLogs(data);
      } catch (error) {
        toast.error('Failed to load client communications');
      } finally {
        setLoadingLogs(false);
      }
    },
    [token]
  );

  const handleReminderOverride = useCallback(
    async (log, status) => {
      if (!log) {
        return;
      }
      if (!token) {
        toast.error('Missing authentication token');
        return;
      }
      let note = log?.metadata?.override_note || '';
      if (status === 'skipped') {
        const input = window.prompt('Optional note for skipping this reminder', note);
        if (input !== null) {
          note = input;
        }
      }
      setOverridingId(log.communication_id);
      try {
        const payload = {
          status,
          note: note && note.trim().length > 0 ? note.trim() : null,
        };
        const updated = await api.post(
          `/accounting/communications/${log.communication_id}/override`,
          payload,
          token
        );
        setCommunicationLogs((prev) =>
          prev.map((item) =>
            item.communication_id === updated.communication_id ? updated : item
          )
        );
        toast.success(
          `Reminder marked as ${formatReminderStatusLabel(`override-${status}`)}`
        );
      } catch (error) {
        console.error(error);
        toast.error('Failed to update reminder status');
      } finally {
        setOverridingId(null);
      }
    },
    [token]
  );

  useEffect(() => {
    if (selectedClientId) {
      loadCommunications(selectedClientId);
    } else {
      setCommunicationLogs([]);
    }
  }, [selectedClientId, loadCommunications]);

  const templateManagerSelectValue = isEditingTemplate
    ? templateForm.template_key || '__new__'
    : '__new__';

  const templateKeySet = useMemo(
    () => new Set(templates.map((template) => template.template_key)),
    [templates]
  );

  const currentEmailTemplate = useMemo(
    () => templates.find((template) => template.template_key === emailForm.template_key) ?? null,
    [templates, emailForm.template_key]
  );

  const currentPortalTemplate = useMemo(
    () => templates.find((template) => template.template_key === portalForm.template_key) ?? null,
    [templates, portalForm.template_key]
  );

  const sortedLogs = useMemo(() => {
    const logsCopy = [...communicationLogs];
    logsCopy.sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
    return logsCopy;
  }, [communicationLogs]);

  const reminderSummary = useMemo(() => {
    return communicationLogs.reduce(
      (acc, log) => {
        const metadata = log.metadata ?? {};
        if (!metadata.reminder_type) {
          return acc;
        }
        acc.total += 1;
        const category = categorizeReminderStatus(metadata.reminder_status);
        acc[category] = (acc[category] ?? 0) + 1;
        return acc;
      },
      { total: 0, pending: 0, sent: 0, skipped: 0 }
    );
  }, [communicationLogs]);

  const queueSummary = useMemo(() => {
    const byChannel = communicationLogs.reduce((acc, log) => {
      const channelKey = log.channel;
      acc[channelKey] = (acc[channelKey] ?? 0) + 1;
      return acc;
    }, {});
    const mostRecent = sortedLogs.length > 0 ? sortedLogs[0].sent_at : null;

    return {
      total: communicationLogs.length,
      byChannel,
      mostRecent,
    };
  }, [communicationLogs, sortedLogs]);

  const workflowBadges = useMemo(
    () => ({
      compose: `${templates.length} template${templates.length === 1 ? '' : 's'}`,
      schedule: automationSettings.enabled ? 'Automation on' : 'Automation off',
      monitor: `${queueSummary.total} record${queueSummary.total === 1 ? '' : 's'}`,
    }),
    [templates.length, automationSettings.enabled, queueSummary.total]
  );

  const isSmtpProvider = emailSettings.provider_type === 'smtp';
  const isSendgridProvider = emailSettings.provider_type === 'sendgrid';

  const updateEmailContext = (index, field, value) => {
    setEmailForm((prev) => {
      const next = prev.context.map((entry, idx) =>
        idx === index ? { ...entry, [field]: value } : entry
      );
      return { ...prev, context: next };
    });
  };

  const addEmailContextRow = () => {
    setEmailForm((prev) => ({
      ...prev,
      context: [...prev.context, createContextRow()],
    }));
  };

  const removeEmailContextRow = (index) => {
    setEmailForm((prev) => {
      const filtered = prev.context.filter((_, idx) => idx !== index);
      return {
        ...prev,
        context: filtered.length > 0 ? filtered : [createContextRow()],
      };
    });
  };

  const updatePortalContext = (index, field, value) => {
    setPortalForm((prev) => {
      const next = prev.context.map((entry, idx) =>
        idx === index ? { ...entry, [field]: value } : entry
      );
      return { ...prev, context: next };
    });
  };

  const addPortalContextRow = () => {
    setPortalForm((prev) => ({
      ...prev,
      context: [...prev.context, createContextRow()],
    }));
  };

  const removePortalContextRow = (index) => {
    setPortalForm((prev) => {
      const filtered = prev.context.filter((_, idx) => idx !== index);
      return {
        ...prev,
        context: filtered.length > 0 ? filtered : [createContextRow()],
      };
    });
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    if (!selectedClientId) {
      toast.error('Select a client before sending communications');
      return;
    }

    const toAddress = emailForm.to_address.trim();
    const templateKey = emailForm.template_key.trim();

    if (!toAddress) {
      toast.error('Recipient email is required');
      return;
    }

    if (!templateKey) {
      toast.error('Choose a template before queuing email delivery');
      return;
    }

    const payload = {
      client_id: selectedClientId,
      to_address: toAddress,
      template_key: templateKey,
      invoice_id: emailForm.invoice_id.trim() || null,
      context: buildContextObject(emailForm.context),
    };

    setSubmittingEmail(true);
    try {
      const response = await api.post(
        '/accounting/communications/email/templated',
        payload,
        token
      );
      toast.success(`Email queued (ID ${response.communication_id})`);
      await loadCommunications(selectedClientId);
      setActiveWorkflowStep('monitor');
      setEmailForm(createEmailForm(templateKey));
    } catch (error) {
      toast.error('Failed to queue templated email');
    } finally {
      setSubmittingEmail(false);
    }
  };

  const handlePortalSubmit = async (event) => {
    event.preventDefault();

    if (!selectedClientId) {
      toast.error('Select a client before logging portal messages');
      return;
    }

    const templateKey = portalForm.template_key.trim();
    if (!templateKey) {
      toast.error('Choose a template before logging a portal message');
      return;
    }

    const payload = {
      client_id: selectedClientId,
      template_key: templateKey,
      invoice_id: portalForm.invoice_id.trim() || null,
      context: buildContextObject(portalForm.context),
    };

    setSubmittingPortal(true);
    try {
      await api.post('/accounting/communications/portal/templated', payload, token);
      toast.success('Portal message logged');
      await loadCommunications(selectedClientId);
      setActiveWorkflowStep('monitor');
      setPortalForm(createPortalForm(templateKey));
    } catch (error) {
      toast.error('Failed to log templated portal message');
    } finally {
      setSubmittingPortal(false);
    }
  };

  if (loading) {
    return <LogoLoader message="Loading communications workspace..." />;
  }

  if (clients.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Client Communications</h1>
          <p className="text-gray-600 mt-1">
            Add clients to begin sending templated emails and portal notifications.
          </p>
        </div>
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No clients available yet. Create a client record to get started.
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedClient = clients.find((client) => client.id === selectedClientId);
  const emailTemplateSelectValue = templateKeySet.has(emailForm.template_key)
    ? emailForm.template_key
    : '__custom__';
  const portalTemplateSelectValue = templateKeySet.has(portalForm.template_key)
    ? portalForm.template_key
    : '__custom__';


  return (
    <div className="space-y-6">
      {helpSearchText && (
        <div className="sr-only" aria-hidden="true">
          {helpSearchText}
        </div>
      )}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Client Communications</h1>
          <p className="mt-1 text-gray-600">
            Coordinate manual outreach, scheduling, and delivery tracking from one workspace.
          </p>
        </div>
        <div className="w-full md:w-72">
          <Label htmlFor="client-select">Client</Label>
          <Select value={selectedClientId} onValueChange={setSelectedClientId}>
            <SelectTrigger id="client-select" className="mt-1">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name} {client.is_active ? '' : '(Inactive)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedClient && (
        <Card>
          <CardContent className="py-4 text-sm text-gray-600">
            Building communications for{' '}
            <span className="font-medium text-gray-900">{selectedClient.name}</span>
            {selectedClient.contact_email && (
              <>
                {' '}
                (<span className="text-gray-700">{selectedClient.contact_email}</span>)
              </>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Workflow navigator</CardTitle>
            <CardDescription>
              Move from drafting to automation and monitoring without changing screens.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {workflowSteps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = activeWorkflowStep === step.id;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => setActiveWorkflowStep(step.id)}
                  className={`w-full rounded-lg border px-4 py-3 text-left transition ${
                    isActive
                      ? 'border-utis-forest bg-utis-forest/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm font-medium ${
                        isActive
                          ? 'border-utis-forest bg-white text-utis-forest'
                          : 'border-gray-300 bg-white text-gray-500'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <StepIcon className={`h-4 w-4 ${isActive ? 'text-utis-forest' : 'text-gray-400'}`} />
                          <span className={`font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                            {step.title}
                          </span>
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            isActive ? 'border-utis-forest text-utis-forest' : 'text-gray-500'
                          }`}
                        >
                          {workflowBadges[step.id]}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">{step.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {activeWorkflowStep === 'compose' && (
            <>
              <Card {...helpAttributes(templateHelp)}>
                <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <CardTitle>Template library</CardTitle>
                    <CardDescription>
                      Maintain reusable copy for both email and portal communications.
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTemplateHelp((prev) => !prev)}
                  >
                    <HelpCircle className="mr-2 h-4 w-4" />
                    {showTemplateHelp ? 'Hide help' : 'Show help'}
                  </Button>
                </CardHeader>
                <CardContent>
                  {showTemplateHelp && templateHelp && (
                    <div className="mb-6 space-y-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
                      <p className="font-medium text-gray-900">{templateHelp.title}</p>
                      <p className="text-sm text-gray-600">{templateHelp.summary}</p>
                      {Array.isArray(templateHelp.steps) && templateHelp.steps.length > 0 && (
                        <ul className="list-disc space-y-1 pl-5">
                          {templateHelp.steps.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ul>
                      )}
                      {Array.isArray(templateHelp.notes) && templateHelp.notes.length > 0 && (
                        <div className="space-y-1">
                          {templateHelp.notes.map((note) => (
                            <p key={note}>{note}</p>
                          ))}
                        </div>
                      )}
                      {Array.isArray(templateHelp.mergeFields) && templateHelp.mergeFields.length > 0 && (
                        <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-700">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {templateHelp.mergeFieldsLabel || 'Merge fields'}
                          </p>
                          <div className="mt-2 grid gap-2 sm:grid-cols-2">
                            {templateHelp.mergeFields.map((keyword) => (
                              <span key={keyword} className="font-mono text-xs text-gray-600">
                                {'{'}
                                {keyword}
                                {'}'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {Array.isArray(templateHelp.keywords) && templateHelp.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {templateHelp.keywords.map((keyword) => (
                            <Badge
                              key={keyword}
                              variant="outline"
                              className="text-[0.65rem] uppercase tracking-wide text-gray-500"
                            >
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <span className="sr-only">{buildHelpSummary(templateHelp)}</span>
                    </div>
                  )}
                  <form className="space-y-6" onSubmit={handleTemplateSubmit}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="manage-template-select">Template</Label>
                        <Select value={templateManagerSelectValue} onValueChange={handleTemplateSelect}>
                          <SelectTrigger id="manage-template-select">
                            <SelectValue placeholder="Select template" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__new__">Create new template</SelectItem>
                            {templates.map((template) => (
                              <SelectItem key={template.template_key} value={template.template_key}>
                                {template.template_key} — {template.subject}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="template-key-input">Template key</Label>
                        <Input
                          id="template-key-input"
                          value={templateForm.template_key}
                          onChange={(event) =>
                            setTemplateForm((prev) => ({ ...prev, template_key: event.target.value }))
                          }
                          placeholder="invoice-reminder"
                          disabled={isEditingTemplate}
                        />
                      </div>
                    </div>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="template-subject-input">Subject</Label>
                        <Input
                          id="template-subject-input"
                          value={templateForm.subject}
                          onChange={(event) =>
                            setTemplateForm((prev) => ({ ...prev, subject: event.target.value }))
                          }
                          placeholder="Payment reminder for {client_name}"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="template-body-input">Body</Label>
                        <Textarea
                          id="template-body-input"
                          value={templateForm.body}
                          onChange={(event) =>
                            setTemplateForm((prev) => ({ ...prev, body: event.target.value }))
                          }
                          placeholder={"Hello {client_name}, your invoice {invoice_id} is ready."}
                          rows={6}
                        />
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <Label className="text-sm font-medium text-gray-900">Attachments</Label>
                          <p className="text-sm text-gray-600">
                            Automatically append supporting documents when this template sends.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id="template-attach-latest-invoice"
                            checked={templateForm.attach_latest_invoice}
                            onCheckedChange={(checked) =>
                              setTemplateForm((prev) => ({
                                ...prev,
                                attach_latest_invoice: Boolean(checked),
                              }))
                            }
                          />
                          <Label htmlFor="template-attach-latest-invoice" className="text-sm text-gray-700">
                            Attach latest invoice PDF
                          </Label>
                        </div>
                      </div>
                      {templateForm.attach_latest_invoice && (
                        <div className="mt-4 space-y-2">
                          <Label htmlFor="template-invoice-filename" className="text-sm font-medium text-gray-900">
                            Filename template (optional)
                          </Label>
                          <Input
                            id="template-invoice-filename"
                            value={templateForm.latest_invoice_filename}
                            onChange={(event) =>
                              setTemplateForm((prev) => ({
                                ...prev,
                                latest_invoice_filename: event.target.value,
                              }))
                            }
                            placeholder="Invoice-{invoice_id}.pdf"
                          />
                          <p className="text-xs text-gray-500">
                            Leave blank to keep the stored document name. Merge variables such as {'{'}invoice_id{'}'} are supported.
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <p className="text-sm text-gray-500">
                        {isEditingTemplate
                          ? 'Updating the selected template will change future communications immediately.'
                          : 'Create a new template to reuse across email and portal communications.'}
                      </p>
                      <div className="flex flex-col gap-2 md:flex-row md:items-center">
                        {isEditingTemplate && (
                          <Button type="button" variant="outline" onClick={startNewTemplate}>
                            Start new template
                          </Button>
                        )}
                        <Button
                          type="submit"
                          className="bg-utis-forest text-white hover:bg-utis-forest/90"
                          disabled={savingTemplate}
                        >
                          {savingTemplate
                            ? isEditingTemplate
                              ? 'Updating…'
                              : 'Creating…'
                            : isEditingTemplate
                            ? 'Update template'
                            : 'Create template'}
                        </Button>
                      </div>
                    </div>
                  </form>
                </CardContent>
              </Card>

              <Tabs defaultValue="email" className="space-y-4">
                <TabsList className="grid grid-cols-2 md:w-auto md:inline-grid">
                  <TabsTrigger value="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Templated Email
                  </TabsTrigger>
                  <TabsTrigger value="portal" className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Portal Message
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="email">
                  <Card {...helpAttributes(templatedEmailHelp)}>
                    <CardHeader>
                      <CardTitle>Queue templated email delivery</CardTitle>
                      <CardDescription>
                        Render templates with merge variables and send using the asynchronous delivery queue.
                        {templatedEmailHelp && (
                          <span className="sr-only">{buildHelpSummary(templatedEmailHelp)}</span>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form className="space-y-6" onSubmit={handleEmailSubmit}>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="email-to">Recipient email</Label>
                            <Input
                              id="email-to"
                              value={emailForm.to_address}
                              onChange={(event) =>
                                setEmailForm((prev) => ({ ...prev, to_address: event.target.value }))
                              }
                              placeholder={selectedClient?.contact_email || 'accounting@client.com'}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email-template">Template</Label>
                            <Select
                              value={emailTemplateSelectValue}
                              onValueChange={(value) => {
                                if (value === '__custom__') {
                                  setEmailForm((prev) => ({ ...prev, template_key: '' }));
                                  return;
                                }
                                setEmailForm((prev) => ({ ...prev, template_key: value }));
                              }}
                            >
                              <SelectTrigger id="email-template">
                                <SelectValue placeholder="Select template" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__custom__">Enter custom template key…</SelectItem>
                                {templates.map((template) => (
                                  <SelectItem key={template.template_key} value={template.template_key}>
                                    {template.template_key} — {template.subject}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email-template-key">Template key</Label>
                            <Input
                              id="email-template-key"
                              value={emailForm.template_key}
                              onChange={(event) =>
                                setEmailForm((prev) => ({ ...prev, template_key: event.target.value }))
                              }
                              placeholder="invoice-reminder"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email-invoice">Invoice (optional)</Label>
                            <Input
                              id="email-invoice"
                              value={emailForm.invoice_id}
                              onChange={(event) =>
                                setEmailForm((prev) => ({ ...prev, invoice_id: event.target.value }))
                              }
                              placeholder="INV-1001"
                            />
                          </div>
                        </div>

                        {currentEmailTemplate && (
                          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
                            <p className="font-semibold text-gray-900">Subject preview</p>
                            <p className="mt-1 text-gray-700">{currentEmailTemplate.subject}</p>
                            <p className="mt-3 font-semibold text-gray-900">Message preview</p>
                            <p className="mt-1 whitespace-pre-wrap text-gray-700">{currentEmailTemplate.body}</p>
                            {Array.isArray(currentEmailTemplate.attachment_rules) &&
                              currentEmailTemplate.attachment_rules.length > 0 && (
                                <div className="mt-3">
                                  <p className="font-semibold text-gray-900">Attachments</p>
                                  <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-700">
                                    {currentEmailTemplate.attachment_rules.map((rule, index) => (
                                      <li key={`${rule.kind}-${index}`}>{describeAttachmentRule(rule)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                          </div>
                        )}

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Template variables</Label>
                            <Button type="button" variant="outline" size="sm" onClick={addEmailContextRow}>
                              <Plus className="mr-2 h-4 w-4" />
                              Add variable
                            </Button>
                          </div>
                          <p className="text-sm text-gray-500">
                            Provide key/value pairs that will replace placeholders such as {'{'}client_name{'}'} in the
                            template.
                          </p>
                          {emailForm.context.map((entry, index) => (
                            <div
                              key={`email-context-${index}`}
                              className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end"
                            >
                              <div>
                                <Label>Variable key</Label>
                                <Input
                                  value={entry.key}
                                  onChange={(event) => updateEmailContext(index, 'key', event.target.value)}
                                  placeholder="client_name"
                                />
                              </div>
                              <div>
                                <Label>Value</Label>
                                <Input
                                  value={entry.value}
                                  onChange={(event) => updateEmailContext(index, 'value', event.target.value)}
                                  placeholder="Acme Corp"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeEmailContextRow(index)}
                                aria-label="Remove variable"
                                disabled={emailForm.context.length === 1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>

                        <div className="flex justify-end">
                          <Button
                            type="submit"
                            className="bg-utis-forest text-white hover:bg-utis-forest/90"
                            disabled={submittingEmail}
                          >
                            {submittingEmail ? 'Queuing email…' : 'Queue Email'}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="portal">
                  <Card {...helpAttributes(portalHelp)}>
                    <CardHeader>
                      <CardTitle>Log templated portal message</CardTitle>
                      <CardDescription>
                        Capture templated messages shared through the client portal for traceability.
                        {portalHelp && <span className="sr-only">{buildHelpSummary(portalHelp)}</span>}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form className="space-y-6" onSubmit={handlePortalSubmit}>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="md:col-span-2 space-y-2">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <Label htmlFor="portal-template">Template</Label>
                              <Select
                                value={portalTemplateSelectValue}
                                onValueChange={(value) => {
                                  if (value === '__custom__') {
                                    setPortalForm((prev) => ({ ...prev, template_key: '' }));
                                    return;
                                  }
                                  setPortalForm((prev) => ({ ...prev, template_key: value }));
                                }}
                              >
                                <SelectTrigger id="portal-template">
                                  <SelectValue placeholder="Select template" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__custom__">Enter custom template key…</SelectItem>
                                  {templates.map((template) => (
                                    <SelectItem key={template.template_key} value={template.template_key}>
                                      {template.template_key} — {template.subject}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <Input
                              value={portalForm.template_key}
                              onChange={(event) => setPortalForm((prev) => ({ ...prev, template_key: event.target.value }))}
                              placeholder="Template key"
                            />
                          </div>
                          <div>
                            <Label htmlFor="portal-invoice">Invoice (optional)</Label>
                            <Input
                              id="portal-invoice"
                              value={portalForm.invoice_id}
                              onChange={(event) => setPortalForm((prev) => ({ ...prev, invoice_id: event.target.value }))}
                              placeholder="INV-1001"
                            />
                          </div>
                        </div>

                        {currentPortalTemplate && (
                          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
                            <p className="font-semibold text-gray-900">Subject preview</p>
                            <p className="mt-1 text-gray-700">{currentPortalTemplate.subject}</p>
                            <p className="mt-3 font-semibold text-gray-900">Message preview</p>
                            <p className="mt-1 whitespace-pre-wrap text-gray-700">{currentPortalTemplate.body}</p>
                          </div>
                        )}

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Template variables</Label>
                            <Button type="button" variant="outline" size="sm" onClick={addPortalContextRow}>
                              <Plus className="mr-2 h-4 w-4" />
                              Add variable
                            </Button>
                          </div>
                          <p className="text-sm text-gray-500">
                            Variables merge into the portal message body and subject when the template is rendered.
                          </p>
                          {portalForm.context.map((entry, index) => (
                            <div
                              key={`portal-context-${index}`}
                              className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end"
                            >
                              <div>
                                <Label>Variable key</Label>
                                <Input
                                  value={entry.key}
                                  onChange={(event) => updatePortalContext(index, 'key', event.target.value)}
                                  placeholder="client_name"
                                />
                              </div>
                              <div>
                                <Label>Value</Label>
                                <Input
                                  value={entry.value}
                                  onChange={(event) => updatePortalContext(index, 'value', event.target.value)}
                                  placeholder="Acme Corp"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removePortalContextRow(index)}
                                aria-label="Remove variable"
                                disabled={portalForm.context.length === 1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>

                        <div className="flex justify-end">
                          <Button
                            type="submit"
                            className="bg-utis-forest text-white hover:bg-utis-forest/90"
                            disabled={submittingPortal}
                          >
                            {submittingPortal ? 'Logging message…' : 'Log Portal Message'}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}

          {activeWorkflowStep === 'schedule' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Email delivery configuration</CardTitle>
                  <CardDescription>
                    Control the sender address and delivery provider used for outbound messages.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {emailSettingsLoading ? (
                    <div className="py-6 text-sm text-gray-500">Loading delivery settings…</div>
                  ) : (
                    <form className="space-y-6" onSubmit={handleEmailSettingsSubmit}>
                      <fieldset className="space-y-6" disabled={savingEmailSettings}>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="email-from-address">From address</Label>
                            <Input
                              id="email-from-address"
                              type="email"
                              value={emailSettings.from_address}
                              onChange={handleEmailFieldChange('from_address')}
                              placeholder="billing@example.com"
                            />
                            <p className="text-xs text-gray-500">
                              Clients will see this address as the sender on automated communications.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email-provider">Delivery provider</Label>
                            <Select
                              value={emailSettings.provider_type}
                              onValueChange={handleProviderChange}
                              disabled={savingEmailSettings}
                            >
                              <SelectTrigger id="email-provider">
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                              <SelectContent>
                                {providerOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">
                              Choose how emails should be dispatched from the platform.
                            </p>
                          </div>
                        </div>

                        {isSmtpProvider && (
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="smtp-host">SMTP host</Label>
                              <Input
                                id="smtp-host"
                                value={emailSettings.credentials.host}
                                onChange={handleCredentialChange('host')}
                                placeholder="smtp.yourprovider.com"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="smtp-port">Port</Label>
                              <Input
                                id="smtp-port"
                                type="number"
                                min="1"
                                value={emailSettings.credentials.port}
                                onChange={handleCredentialChange('port')}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="smtp-username">Username</Label>
                              <Input
                                id="smtp-username"
                                value={emailSettings.credentials.username}
                                onChange={handleCredentialChange('username')}
                                placeholder="api-user"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="smtp-password">Password</Label>
                              <Input
                                id="smtp-password"
                                type="password"
                                value={emailSettings.credentials.password}
                                onChange={handleCredentialChange('password')}
                                placeholder="••••••••"
                              />
                            </div>
                            <div className="md:col-span-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">Enable TLS</p>
                                <p className="text-xs text-gray-500">
                                  Start a secure TLS connection before authenticating with the SMTP server.
                                </p>
                              </div>
                              <Switch
                                checked={Boolean(emailSettings.credentials.use_tls)}
                                onCheckedChange={handleCredentialToggle('use_tls')}
                                disabled={savingEmailSettings}
                                aria-label="Toggle TLS"
                              />
                            </div>
                          </div>
                        )}

                        {isSendgridProvider && (
                          <div className="space-y-2">
                            <Label htmlFor="sendgrid-api-key">SendGrid API key</Label>
                            <Input
                              id="sendgrid-api-key"
                              type="password"
                              value={emailSettings.credentials.api_key}
                              onChange={handleCredentialChange('api_key')}
                              placeholder="SG.xxxxx"
                            />
                            <p className="text-xs text-gray-500">
                              Stored securely and used when calling the SendGrid mail API.
                            </p>
                          </div>
                        )}
                      </fieldset>

                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          className="bg-utis-forest text-white hover:bg-utis-forest/90"
                          disabled={savingEmailSettings || !emailSettingsDirty}
                        >
                          {savingEmailSettings ? 'Saving settings…' : 'Save email settings'}
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>

              <Card {...helpAttributes(automationHelp)}>
                <CardHeader>
                  <CardTitle>Automated email triggers</CardTitle>
                  <CardDescription>
                    Determine when the system should queue accounting reminders automatically.
                    {automationHelp && <span className="sr-only">{buildHelpSummary(automationHelp)}</span>}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {automationLoading ? (
                    <div className="py-6 text-sm text-gray-500">Loading automation settings…</div>
                  ) : (
                    <form className="space-y-6" onSubmit={handleAutomationSave}>
                      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Automation status</p>
                          <p className="text-sm text-gray-600">
                            Toggle to allow the platform to send reminders without manual intervention.
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">
                            {automationSettings.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <Switch
                            checked={automationSettings.enabled}
                            onCheckedChange={handleAutomationToggle}
                            aria-label="Toggle automated email delivery"
                          />
                        </div>
                      </div>

                      <fieldset className="space-y-6" disabled={!automationSettings.enabled}>
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-900">Invoice status triggers</Label>
                          <p className="text-xs text-gray-500">
                            Reminder emails queue automatically when an invoice enters a selected status.
                          </p>
                          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {invoiceStatusOptions.map((option) => {
                              const checked = automationSettings.trigger_invoice_statuses.includes(option.value);
                              return (
                                <label
                                  key={option.value}
                                  className={`flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm ${
                                    automationSettings.enabled ? 'text-gray-700' : 'text-gray-400'
                                  }`}
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={handleAutomationStatusChange(option.value)}
                                    disabled={!automationSettings.enabled}
                                  />
                                  <span>{option.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="space-y-2">
                            <Label htmlFor="automation-days-before">Days before due date</Label>
                            <Input
                              id="automation-days-before"
                              type="number"
                              min="0"
                              value={automationSettings.days_before_due ?? ''}
                              onChange={handleAutomationNumberChange('days_before_due')}
                              placeholder="e.g. 5"
                              disabled={!automationSettings.enabled}
                            />
                            <p className="text-xs text-gray-500">
                              Queue reminders this many days prior to the invoice due date.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="automation-days-after">Days after due date</Label>
                            <Input
                              id="automation-days-after"
                              type="number"
                              min="0"
                              value={automationSettings.days_after_due ?? ''}
                              onChange={handleAutomationNumberChange('days_after_due')}
                              placeholder="e.g. 2"
                              disabled={!automationSettings.enabled}
                            />
                            <p className="text-xs text-gray-500">
                              Continue sending reminders once invoices become overdue.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="automation-po-hours">PO remaining hours threshold</Label>
                            <Input
                              id="automation-po-hours"
                              type="number"
                              min="0"
                              step="0.1"
                              value={automationSettings.po_hours_remaining_threshold ?? ''}
                              onChange={handleAutomationNumberChange('po_hours_remaining_threshold')}
                              placeholder="e.g. 12"
                              disabled={!automationSettings.enabled}
                            />
                            <p className="text-xs text-gray-500">
                              Trigger alerts when purchase orders approach the remaining hours limit.
                            </p>
                          </div>
                        </div>
                      </fieldset>

                      <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Payment acknowledgements</p>
                            <p className="text-sm text-gray-600">
                              Automatically thank clients when manual or gateway payments are recorded.
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                              {automationSettings.payment_acknowledgements_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <Switch
                              checked={automationSettings.payment_acknowledgements_enabled}
                              onCheckedChange={handleAcknowledgementToggle}
                              aria-label="Toggle automatic payment acknowledgements"
                            />
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="ack-template">Template key</Label>
                            <Input
                              id="ack-template"
                              value={automationSettings.payment_acknowledgement_template}
                              onChange={handleAcknowledgementTemplateChange}
                              placeholder="invoice-payment-received"
                            />
                            <p className="text-xs text-gray-500">
                              Choose which email template renders acknowledgement messages.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label>Client overrides</Label>
                            <div className="max-h-48 space-y-2 overflow-y-auto rounded border border-gray-200 p-3">
                              {clients.length === 0 ? (
                                <p className="text-xs text-gray-500">No clients available.</p>
                              ) : (
                                clients
                                  .filter((client) => client.is_active)
                                  .map((client) => {
                                    const enabled = resolveClientAcknowledgement(client.id);
                                    return (
                                      <div
                                        key={client.id}
                                        className="flex items-center justify-between gap-3"
                                      >
                                        <span className="text-sm text-gray-700">{client.name}</span>
                                        <Switch
                                          checked={enabled}
                                          onCheckedChange={handleAcknowledgementOverrideToggle(client.id)}
                                          aria-label={`Toggle acknowledgements for ${client.name}`}
                                        />
                                      </div>
                                    );
                                  })
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {automationSettings.payment_acknowledgements_enabled
                                ? 'Disable acknowledgements for specific clients while global delivery stays on.'
                                : 'Enable acknowledgements for select clients even when the global toggle is off.'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          className="bg-utis-forest text-white hover:bg-utis-forest/90"
                          disabled={savingAutomation || !automationDirty}
                        >
                          {savingAutomation ? 'Saving automation…' : 'Save automation settings'}
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>

              <Card {...helpAttributes(queueHelp)}>
                <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <CardTitle>Queue activity snapshot</CardTitle>
                    <CardDescription>
                      Review recent deliveries to confirm the schedule aligns with expectations.
                      {queueHelp && <span className="sr-only">{buildHelpSummary(queueHelp)}</span>}
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => loadCommunications(selectedClientId)}
                    disabled={loadingLogs}
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${loadingLogs ? 'animate-spin' : ''}`} />
                    Refresh activity
                  </Button>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total logged</p>
                      <p className="mt-2 text-2xl font-semibold text-gray-900">{queueSummary.total}</p>
                      <p className="text-sm text-gray-500">Communications recorded for this client.</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Automation</p>
                      <p className="mt-2 text-2xl font-semibold text-gray-900">
                        {automationSettings.enabled ? 'On' : 'Off'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {automationSettings.enabled
                          ? 'Reminders will queue when trigger conditions are met.'
                          : 'Automation is disabled; reminders must be sent manually.'}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last activity</p>
                      <p className="mt-2 text-lg font-semibold text-gray-900">
                        {formatDisplayDateTime(queueSummary.mostRecent, 'No activity yet')}
                      </p>
                      <p className="text-sm text-gray-500">Most recent send captured in the log.</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-900">Volume by channel</p>
                    <p className="text-sm text-gray-500">
                      Balance email and portal touchpoints as you plan follow-ups.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(queueSummary.byChannel).length === 0 ? (
                        <span className="text-sm text-gray-500">No communications recorded yet.</span>
                      ) : (
                        Object.entries(queueSummary.byChannel).map(([channel, count]) => (
                          <Badge key={channel} variant="outline" className="font-normal">
                            {channel.charAt(0).toUpperCase() + channel.slice(1)} • {count}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-900">Reminder status</p>
                    <p className="text-sm text-gray-500">
                      Monitor automated reminders alongside any manual overrides.
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Pending
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-gray-900">
                          {reminderSummary.pending}
                        </p>
                        <p className="text-xs text-gray-500">
                          Awaiting manual action or delivery.
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Sent
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-gray-900">
                          {reminderSummary.sent}
                        </p>
                        <p className="text-xs text-gray-500">
                          Logged reminders delivered automatically.
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Skipped
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-gray-900">
                          {reminderSummary.skipped}
                        </p>
                        <p className="text-xs text-gray-500">
                          Overrides or auto-skipped reminders.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {activeWorkflowStep === 'monitor' && (
            <Card {...helpAttributes(historyHelp)}>
              <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Communication history</CardTitle>
                  <CardDescription>
                    Combined log of all emails and portal updates sent to this client.
                    {historyHelp && <span className="sr-only">{buildHelpSummary(historyHelp)}</span>}
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => loadCommunications(selectedClientId)}
                  disabled={loadingLogs}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${loadingLogs ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Channel</TableHead>
                      <TableHead>Template</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Context</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Reminder</TableHead>
                    <TableHead>Sent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingLogs ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-gray-500">
                          Loading communications…
                        </TableCell>
                      </TableRow>
                    ) : sortedLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-gray-500">
                          No communications logged yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedLogs.map((log) => {
                        const metadata = log.metadata ?? {};
                        const contextPairs = parseContextMetadata(metadata);
                        const otherMetadata = Object.entries(metadata).filter(
                          ([key]) =>
                            ![
                              'context',
                              'subject',
                              'message',
                              'to',
                              'from',
                              'attachments',
                              'reminder_type',
                              'reminder_status',
                              'reminder_reference',
                              'reminder_queue_id',
                              'override_note',
                              'override_updated_at',
                              'queued_at',
                              'updated_at',
                              'deliveries',
                              'reason',
                              'po_id',
                            ].includes(key)
                        );
                        const channelStyle =
                          channelStyles[log.channel] ?? 'bg-gray-100 text-gray-700 border-gray-200';
                        const reminderType = metadata.reminder_type || '';
                        const reminderStatus = metadata.reminder_status || '';
                        const reminderReference = metadata.reminder_reference || '';
                        const overrideNote = metadata.override_note || '';
                        const reminderCategory = categorizeReminderStatus(reminderStatus);
                        const isReminder = Boolean(reminderType);
                        const showSpinner = overridingId === log.communication_id;

                        return (
                          <TableRow key={log.communication_id}>
                            <TableCell>
                              <Badge variant="outline" className={`${channelStyle} border`}>
                                {log.channel.charAt(0).toUpperCase() + log.channel.slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium text-gray-900">{log.template_key}</TableCell>
                            <TableCell>{metadata.subject || '—'}</TableCell>
                            <TableCell>
                              <div className="space-y-1 text-sm text-gray-700">
                                {metadata.from && <div>From: {metadata.from}</div>}
                                {metadata.to && <div>To: {metadata.to}</div>}
                                {metadata.message && (
                                  <p className="whitespace-pre-wrap text-gray-700">{metadata.message}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                {contextPairs.map(([key, value]) => (
                                  <Badge
                                    key={`context-${log.communication_id}-${key}`}
                                    variant="outline"
                                    className="font-normal"
                                  >
                                    {key}: {value}
                                  </Badge>
                                ))}
                                {otherMetadata.map(([key, value]) => (
                                  <Badge
                                    key={`meta-${log.communication_id}-${key}`}
                                    variant="outline"
                                    className="font-normal"
                                  >
                                    {key}: {value}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>{log.invoice_id || '—'}</TableCell>
                            <TableCell>
                              {isReminder ? (
                                <div className="space-y-2 text-sm text-gray-700">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant="outline" className="font-normal">
                                      {formatReminderTypeLabel(reminderType)}
                                    </Badge>
                                    <Badge
                                      variant={
                                        reminderCategory === 'pending'
                                          ? 'secondary'
                                          : reminderCategory === 'skipped'
                                            ? 'destructive'
                                            : 'outline'
                                      }
                                      className="font-normal"
                                    >
                                      {formatReminderStatusLabel(reminderStatus)}
                                    </Badge>
                                  </div>
                                  {reminderReference && (
                                    <p className="text-xs text-gray-500">
                                      Reference: {reminderReference}
                                    </p>
                                  )}
                                  {overrideNote && (
                                    <p className="text-xs italic text-gray-500">
                                      Note: {overrideNote}
                                    </p>
                                  )}
                                  {reminderCategory === 'pending' && (
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        variant="outline"
                                        size="xs"
                                        onClick={() => handleReminderOverride(log, 'sent')}
                                        disabled={showSpinner}
                                      >
                                        {showSpinner && (
                                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                        )}
                                        Mark sent
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="xs"
                                        onClick={() => handleReminderOverride(log, 'skipped')}
                                        disabled={showSpinner}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        {showSpinner && (
                                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                        )}
                                        Skip
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">—</span>
                              )}
                            </TableCell>
                            <TableCell>{formatDisplayDateTime(log.sent_at, '—')}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Communications;
